/**
 * The sections of song lyrics for the lyric line picker of the Studio (design `studio-details`
 * frame b). Pure: no DOM, no network.
 *
 * - A line like `[Chorus]` starts a section. It is a header, not a line.
 * - Before the first label, a blank line ends a section ("Part 1", "Part 2": the label is null).
 * - A section with the same lines as an earlier section shows one time; the earlier one counts it.
 *   A label with no lines (`[Chorus]` alone) repeats the earlier section with that label.
 * - A line has 3 to 60 characters (`LYRIC_LINE_MIN` / `LYRIC_LINE_MAX`). Shorter and longer lines
 *   are not pickable, so they do not show.
 */

export const LYRIC_LINE_MIN = 3;
export const LYRIC_LINE_MAX = 60;
/** The most lines of the "Suggested" group. */
export const SUGGESTED_MAX = 4;
/** A "Short" line has this many characters or more... */
export const SHORT_MIN = 12;
/** ...and this many characters or fewer. */
export const SHORT_MAX = 40;

export interface LyricSection {
  /** The text of the bracket label ("Chorus"). Null: the lyrics have no label here ("Part n"). */
  label: string | null;
  /** The lines of the section, trimmed, in order. */
  lines: string[];
  /** How many times the section occurs in the song (1: one time). */
  repeat: number;
}

export type LyricSuggestionReason = 'hook' | 'repeated' | 'short';

export interface LyricSuggestion {
  line: string;
  reason: LyricSuggestionReason;
  /** How many times the line occurs in the song, the section repeats included. */
  count: number;
  /** The section of the first occurrence of the line. */
  sectionIndex: number;
  /** The index of the line in that section. */
  lineIndex: number;
}

const LABEL = /^\[(.*)\]$/;

/** The key that compares two lines: lower case, one space, no end punctuation. */
export function lyricLineKey(line: string): string {
  return line
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.,!?;:…]+$/u, '')
    .trim();
}

function isPickable(line: string): boolean {
  return line.length >= LYRIC_LINE_MIN && line.length <= LYRIC_LINE_MAX;
}

function sectionKey(lines: string[]): string {
  return lines.map(lyricLineKey).join('\n');
}

interface RawSection {
  label: string | null;
  lines: string[];
}

function rawSections(lyrics: string): RawSection[] {
  const sections: RawSection[] = [];
  let current: RawSection | null = null;
  let labelled = false;
  for (const rawLine of lyrics.split(/\r?\n/)) {
    const line = rawLine.trim();
    const label = LABEL.exec(line);
    if (label !== null) {
      const text = (label[1] ?? '').trim();
      current = { label: text === '' ? null : text, lines: [] };
      sections.push(current);
      labelled = true;
      continue;
    }
    if (line === '') {
      // Before the first label a blank line ends the section. After a label, the label holds.
      if (!labelled) {
        current = null;
      }
      continue;
    }
    if (current === null) {
      current = { label: null, lines: [] };
      sections.push(current);
    }
    if (isPickable(line)) {
      current.lines.push(line);
    }
  }
  return sections;
}

/** The sections of the lyrics, with the repeated sections shown one time. Null or blank: none. Pure. */
export function lyricSections(lyrics: string | null): LyricSection[] {
  if (lyrics === null || lyrics.trim() === '') {
    return [];
  }
  const result: LyricSection[] = [];
  for (const raw of rawSections(lyrics)) {
    if (raw.lines.length === 0) {
      // `[Chorus]` alone: the earlier chorus repeats. An unknown label with no lines does not show.
      const earlier =
        raw.label === null
          ? undefined
          : result.find((section) => section.label?.toLowerCase() === raw.label?.toLowerCase());
      if (earlier !== undefined) {
        earlier.repeat += 1;
      }
      continue;
    }
    const key = sectionKey(raw.lines);
    const same = result.find((section) => sectionKey(section.lines) === key);
    if (same !== undefined) {
      same.repeat += 1;
      continue;
    }
    result.push({ label: raw.label, lines: raw.lines, repeat: 1 });
  }
  return result;
}

/** The number of different lines of the sections. */
export function lyricLineCount(sections: ReadonlyArray<LyricSection>): number {
  const keys = new Set<string>();
  sections.forEach((section) => section.lines.forEach((line) => keys.add(lyricLineKey(line))));
  return keys.size;
}

interface LineStat {
  line: string;
  count: number;
  sectionIndex: number;
  lineIndex: number;
  order: number;
}

function lineStats(sections: ReadonlyArray<LyricSection>): LineStat[] {
  const stats = new Map<string, LineStat>();
  let order = 0;
  sections.forEach((section, sectionIndex) => {
    section.lines.forEach((line, lineIndex) => {
      const key = lyricLineKey(line);
      const stat = stats.get(key);
      if (stat === undefined) {
        stats.set(key, { line, count: section.repeat, sectionIndex, lineIndex, order });
        order += 1;
      } else {
        stat.count += section.repeat;
      }
    });
  });
  return [...stats.values()];
}

/**
 * The "Suggested" lines (at most `SUGGESTED_MAX`, no line two times): the hook (the line that
 * repeats most), then the other lines that repeat (most first), then short lines that fit big
 * (`SHORT_MIN` to `SHORT_MAX` characters, shortest first). Pure.
 */
export function suggestedLines(sections: ReadonlyArray<LyricSection>, max: number = SUGGESTED_MAX): LyricSuggestion[] {
  const stats = lineStats(sections);
  const byCount = stats
    .filter((stat) => stat.count >= 2)
    .sort((a, b) => (b.count !== a.count ? b.count - a.count : a.order - b.order));
  const suggestions: LyricSuggestion[] = [];
  const toSuggestion = (stat: LineStat, reason: LyricSuggestionReason): LyricSuggestion => ({
    line: stat.line,
    reason,
    count: stat.count,
    sectionIndex: stat.sectionIndex,
    lineIndex: stat.lineIndex,
  });
  byCount.forEach((stat, index) => {
    suggestions.push(toSuggestion(stat, index === 0 ? 'hook' : 'repeated'));
  });
  // Keep a place for a short line when the song has one.
  const shorts = stats
    .filter((stat) => stat.count < 2 && stat.line.length >= SHORT_MIN && stat.line.length <= SHORT_MAX)
    .sort((a, b) => (a.line.length !== b.line.length ? a.line.length - b.line.length : a.order - b.order));
  const repeatedRoom = shorts.length > 0 ? max - 1 : max;
  const result = suggestions.slice(0, repeatedRoom);
  for (const stat of shorts) {
    if (result.length >= max) {
      break;
    }
    result.push(toSuggestion(stat, 'short'));
  }
  return result;
}

/** The stored value of one or two lines: two lines are joined with a line break. */
export function joinLyricLines(first: string, second?: string | null): string {
  const a = first.trim();
  const b = (second ?? '').trim();
  return b === '' ? a : `${a}\n${b}`;
}

/** The lines of a stored value: one or two trimmed lines; an empty value gives none. */
export function splitLyricLine(value: string | null | undefined): string[] {
  if (value === null || value === undefined) {
    return [];
  }
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .slice(0, 2);
}

/**
 * The two lines that a pick in "2 lines" gives: the line and the next line of its section. The
 * last line of a section pairs with the line before it. A section of one line gives that line. Pure.
 */
export function lyricPairAt(lines: ReadonlyArray<string>, index: number): string {
  const line = lines[index];
  if (line === undefined) {
    return '';
  }
  const next = lines[index + 1];
  if (next !== undefined) {
    return joinLyricLines(line, next);
  }
  const before = lines[index - 1];
  return before === undefined ? line : joinLyricLines(before, line);
}

/** Where a line first occurs: the section and the line index. Null: not found. */
export function findLyricLine(
  sections: ReadonlyArray<LyricSection>,
  line: string
): { sectionIndex: number; lineIndex: number } | null {
  for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex += 1) {
    const lineIndex = sections[sectionIndex]?.lines.indexOf(line) ?? -1;
    if (lineIndex !== -1) {
      return { sectionIndex, lineIndex };
    }
  }
  return null;
}

export interface LyricSearchHit {
  line: string;
  sectionIndex: number;
  lineIndex: number;
  /** The start of the match in the line. */
  matchStart: number;
}

/** The lines that hold the query, in any case. A repeated line shows one time. Empty query: none. Pure. */
export function searchLyricLines(sections: ReadonlyArray<LyricSection>, query: string): LyricSearchHit[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') {
    return [];
  }
  const seen = new Set<string>();
  const hits: LyricSearchHit[] = [];
  sections.forEach((section, sectionIndex) => {
    section.lines.forEach((line, lineIndex) => {
      const key = lyricLineKey(line);
      const matchStart = line.toLowerCase().indexOf(needle);
      if (matchStart === -1 || seen.has(key)) {
        return;
      }
      seen.add(key);
      hits.push({ line, sectionIndex, lineIndex, matchStart });
    });
  });
  return hits;
}
